import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AuthCallback } from "@/components/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { DepotProvider } from "@/context/DepotContext";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import CoordinatorDashboard from "@/pages/CoordinatorDashboard";
import WorkerDashboard from "@/pages/WorkerDashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import PayInvoice from "@/pages/PayInvoice";
import JoinCrew from "@/pages/JoinCrew";
import JoinPoster from "@/pages/JoinPoster";
import { SEO } from "@/seo/useSeo";
import { Loader2 } from "lucide-react";
import { rememberNext } from "@/lib/utils";
import { ConnectionRetry } from "@/components/ConnectionRetry";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Redirect but keep ?gclid / utm tags so ad clicks still count.
const KeepQuery = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

const Gate = () => {
  const { user, loading, offline, retry, logout } = useAuth();
  useEffect(() => { if (user) document.title = "Cross Jobs"; }, [user]);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user && offline) return <ConnectionRetry onRetry={retry} onSignOut={logout} />;
  if (!user) { rememberNext(); return isStandalone() ? <Navigate to="/login" replace /> : <Home />; }
  if (user.role === "owner") return <OwnerDashboard />;
  return user.role === "coordinator" ? <CoordinatorDashboard /> : <WorkerDashboard />;
};

// Detect the Google OAuth callback (session_id in the URL fragment) during render,
// BEFORE the auth gate runs, to avoid a race with the /me check.
const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/home" element={<Home />} />
      <Route path="/pay/:token" element={<PayInvoice />} />
      <Route path="/join/:code" element={<JoinCrew />} />
      <Route path="/join/:code/poster" element={<JoinPoster />} />
      {[...SEO.site.nav, ...SEO.site.industries].map(([path]) => <Route key={path} path={path} element={<Landing path={path} />} />)}
      {[...SEO.site.nav, ...SEO.site.industries].map(([path]) => <Route key={`${path}/*`} path={`${path}/*`} element={<KeepQuery to={path} />} />)}
      <Route path="/" element={<Gate />} />
      <Route path="*" element={<KeepQuery to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <CompanyProvider>
          <DepotProvider>
          <div className="App">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
            <InstallPrompt />
          </div>
          </DepotProvider>
        </CompanyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
